import { useEffect, useMemo, useCallback, memo } from "react";
import PropTypes from "prop-types";
import _isEmpty from "lodash/isEmpty";
import { getDirtyFields } from "../../utils/formValidationUtils";
import AutoSaveManager from "./AutoSaveManager";

const AutoSave = ({
  formik,
  onSave,
  noDelayFields,
  delay,
  flushAutoSave,
  setFlushAutoSave,
  deferred,
}) => {
  const autoSaveManager = useMemo(() => new AutoSaveManager(delay), [delay]);
  const performSave = useCallback(
    values => {
      formik.setStatus("submitted");

      const handleSave = errors => {
        if (!_isEmpty(errors)) return;
        onSave(values, formik);
        const { values: formikValues, resetForm } = formik;
        resetForm({ values: formikValues });
        autoSaveManager.bulkData = {};
      };
      formik.validateForm(values).then(handleSave);
    },
    [autoSaveManager, formik, onSave]
  );

  useEffect(() => {
    autoSaveManager.setOptions(performSave, noDelayFields);
  }, [autoSaveManager, noDelayFields, performSave]);

  useEffect(() => {
    const values = getDirtyFields(formik);
    autoSaveManager.push(values);
  }, [autoSaveManager, formik]);

  // Sync deferred mode into the manager whenever it changes.
  // When deferred switches false → true  : cancels the debounce timer.
  // When deferred switches true  → false : flushes any accumulated pendingData.
  useEffect(() => {
    autoSaveManager.setDeferred(!!deferred);
  }, [autoSaveManager, deferred]);

  // Flush pending data on unmount so changes are never silently dropped.
  useEffect(() => {
    return () => {
      autoSaveManager.setDeferred(false);
    };
  }, [autoSaveManager]);

  useEffect(() => {
    if (!flushAutoSave) setFlushAutoSave(() => autoSaveManager.flushAutoSave());
  }, [autoSaveManager, flushAutoSave, setFlushAutoSave]);

  return null;
};

AutoSave.defaultProps = { noDelayFields: [], deferred: false };
AutoSave.propTypes = {
  formik: PropTypes.object,
  onSave: PropTypes.func,
  noDelayFields: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)])
  ),
  flushAutoSave: PropTypes.func,
  setFlushAutoSave: PropTypes.func,
  deferred: PropTypes.bool,
};

export default memo(AutoSave);
